import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { port } from '../../App';
import { toast } from 'react-toastify';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Modal, OverlayTrigger, Tooltip } from 'react-bootstrap';
import SendWhatsAppModal from '../../Components/WhatsApp/SendWhatsAppModal';

const LocalLoader = () => (
    <div className="flex justify-center items-center py-8">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-indigo-600"></div>
    </div>
);

const INTERVIEW_STAGES = [
    { code: 'follow_up', display_name: 'Follow Up' },
    { code: 'consider_to_client', display_name: 'Consider to Client' },
    { code: 'internal_hired', display_name: 'Internal Hired' },
    { code: 'closed', display_name: 'Closed' }
];

const CLIENT_STAGES = [
    { code: 'follow_up', display_name: 'Follow Up' },
    { code: 'consider_to_client', display_name: 'Consider to Client' },
    { code: 'prospect', display_name: 'Prospect' },
    { code: 'closed', display_name: 'Closed' }
];

const SUB_STATUSES = {
    follow_up: [
        { code: 'call_notpicked', display_name: 'Call Not Picked' },
        { code: 'dis_connect', display_name: 'Disconnect' },
        { code: 'will_revert_back', display_name: 'Will Revert Back' },
        { code: 'interview_scheduled', display_name: 'Interview Scheduled' },
        { code: 'interview_attended', display_name: 'Interview Attended' }
    ],
    consider_to_client: [
        { code: 'offered', display_name: 'Offered' },
        { code: 'joined', display_name: 'Joined' },
        { code: 'rejected', display_name: 'Rejected' },
        { code: 'rejected_by_candidate', display_name: 'Rejected by Candidate' }
    ],
    internal_hired: [
        { code: 'offered', display_name: 'Offered' },
        { code: 'joined', display_name: 'Joined' },
        { code: 'rejected', display_name: 'Rejected' },
        { code: 'rejected_by_candidate', display_name: 'Rejected by Candidate' }
    ]
};

const LeadsDashboard = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    // Session user details
    const user = JSON.parse(sessionStorage.getItem('user'));
    const loginEmpId = user?.EmployeeId;
    const isManagerOrAdmin = user?.Disgnation === 'Admin' || user?.Disgnation === 'HR' || user?.Designation === 'Admin' || user?.Designation === 'HR';
    const canAssignLeads = isManagerOrAdmin || user?.user_permissions?.lead_assign_access;

    // State management
    const [leads, setLeads] = useState([]);
    const [recruiters, setRecruiters] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    // Pagination state
    const [page, setPage] = useState(() => parseInt(searchParams.get('page')) || 1);
    const [pageSize, setPageSize] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [totalPages, setTotalPages] = useState(1);

    // Filter and search state
    const [search, setSearch] = useState(() => searchParams.get('search') || '');
    const [searchInput, setSearchInput] = useState(() => searchParams.get('search') || '');
    const [sortBy, setSortBy] = useState(() => searchParams.get('sort_by') || '-Created_Date');
    const [filterType, setFilterType] = useState(() => searchParams.get('filter_type') || 'today');
    const [customStartDate, setCustomStartDate] = useState(() => searchParams.get('start_date') || '');
    const [customEndDate, setCustomEndDate] = useState(() => searchParams.get('end_date') || '');
    const [startTime, setStartTime] = useState(() => searchParams.get('start_time') || '');
    const [endTime, setEndTime] = useState(() => searchParams.get('end_time') || '');

    // Timeline Selection Modal states
    const [showTimelineModal, setShowTimelineModal] = useState(false);
    const [localFilterType, setLocalFilterType] = useState(filterType);
    const [localStartDate, setLocalStartDate] = useState(customStartDate);
    const [localEndDate, setLocalEndDate] = useState(customEndDate);
    const [localStartTime, setLocalStartTime] = useState(startTime);
    const [localEndTime, setLocalEndTime] = useState(endTime);

    // Multi-selection state
    const [selectedLeads, setSelectedLeads] = useState([]);
    const [selectedLeadsData, setSelectedLeadsData] = useState({});
    const [showWAModal, setShowWAModal] = useState(false);
    const [assignTargetRecruiter, setAssignTargetRecruiter] = useState('');

    // Bulk upload states
    const [uploading, setUploading] = useState(false);

    // Mock/UI-only priority state to align with the CRM mockup
    const [leadPriorities, setLeadPriorities] = useState({});
    const [stages, setStages] = useState([]);

    // Action Modal state (Edit Candidate Lead)
    const [showActionModal, setShowActionModal] = useState(false);
    const [selectedLeadForModal, setSelectedLeadForModal] = useState(null);
    const [modalCandidateName, setModalCandidateName] = useState('');
    const [modalCandidateEmail, setModalCandidateEmail] = useState('');
    const [modalCandidatePhone, setModalCandidatePhone] = useState('');
    const [modalCandidateDesignation, setModalCandidateDesignation] = useState('');
    const [modalSource, setModalSource] = useState('');
    const [modalRemarks, setModalRemarks] = useState('');
    const [modalPriority, setModalPriority] = useState('medium');
    const [modalRecruiter, setModalRecruiter] = useState('');
    const [isSavingModal, setIsSavingModal] = useState(false);

    // Inline Status Change / Review Modal state
    const [showReviewModal, setShowReviewModal] = useState(false);
    const [reviewLead, setReviewLead] = useState(null);
    const [reviewNewStage, setReviewNewStage] = useState('');
    const [reviewCurrentStatus, setReviewCurrentStatus] = useState('');
    const [reviewRemarks, setReviewRemarks] = useState('');
    const [reviewScheduleFollowup, setReviewScheduleFollowup] = useState(false);
    const [reviewFollowupDate, setReviewFollowupDate] = useState('');
    const [reviewFollowupTime, setReviewFollowupTime] = useState('');
    const [reviewFollowupNotes, setReviewFollowupNotes] = useState('');
    const [reviewInterviewDate, setReviewInterviewDate] = useState('');
    const [reviewIsInterview, setReviewIsInterview] = useState(false);
    const [isSavingReview, setIsSavingReview] = useState(false);

    // Dedicated Follow-up Modal state
    const [showFollowupModal, setShowFollowupModal] = useState(false);
    const [followupLead, setFollowupLead] = useState(null);
    const [followupDate, setFollowupDate] = useState('');
    const [followupTime, setFollowupTime] = useState('');
    const [followupNotes, setFollowupNotes] = useState('');
    const [isSavingFollowup, setIsSavingFollowup] = useState(false);
    const [followupIsInterview, setFollowupIsInterview] = useState(false);


    // Dropdown state
    const [activeDropdownLeadId, setActiveDropdownLeadId] = useState(null);
    const [activeDropdownInterviewId, setActiveDropdownInterviewId] = useState(null);

    const handleInterviewAttendanceSelect = async (lead, newStatus) => {
        try {
            await axios.patch(`${port}/root/create-daily-achieved-activity/${lead.id}`, {
                interview_attendance: newStatus
            });
            toast.success(`Interview status updated to ${newStatus}`);
            fetchLeads();
        } catch (err) {
            console.error("Error updating interview status:", err);
            toast.error("Failed to update interview status");
        }
    };

    // Column wise filters — keys used for both state and URL (prefixed with cf_ in URL)
    const colFilterKeys = ['candidate_name', 'id', 'candidate_email', 'candidate_phone', 'Created_Date', 'source', 'candidate_designation', 'lead_stage', 'current_status', 'interview_attendance', 'interview_status', 'interview_scheduled_date', 'handled_by', 'assigned_by', 'interview_call_remarks', 'next_followup'];

    // Initialise from URL search params (cf_ prefix)
    const buildColFiltersFromURL = () => {
        const filters = {};
        colFilterKeys.forEach(key => {
            filters[key] = searchParams.get(`cf_${key}`) || '';
        });
        return filters;
    };

    const [colFilters, setColFilters] = useState(buildColFiltersFromURL);
    const colFiltersInitRef = useRef(true);

    // Sync colFilters → URL (debounced)
    useEffect(() => {
        // Skip the initial render to avoid overwriting URL on mount
        if (colFiltersInitRef.current) {
            colFiltersInitRef.current = false;
            return;
        }
        const timer = setTimeout(() => {
            const params = new URLSearchParams(searchParams);
            // Remove old cf_ params first
            [...params.keys()].forEach(k => { if (k.startsWith('cf_')) params.delete(k); });
            // Add active column filters
            Object.entries(colFilters).forEach(([key, val]) => {
                if (val) params.set(`cf_${key}`, val);
            });
            // Reset to page 1 on column filter change
            params.set('page', '1');
            setSearchParams(params, { replace: true });
        }, 400);
        return () => clearTimeout(timer);
    }, [colFilters]);

    const clearColFilters = () => {
        const empty = {};
        colFilterKeys.forEach(key => { empty[key] = ''; });
        setColFilters(empty);
    };

    const isAnyColFilterActive = Object.values(colFilters).some(val => val !== '');

    const getFilterTypeDisplayName = () => {
        const mapping = {
            'today': 'Today',
            'yesterday': 'Yesterday',
            'this_week': 'This Week',
            'last_week': 'Last Week',
            'this_month': 'This Month',
            'prev_month': 'Last Month',
            'all': 'Timeline',
            'all_time': 'Timeline',
            'timeline': 'Timeline',
            'custom': 'Custom Range'
        };
        return mapping[filterType] || 'Timeline';
    };

    const getCalculatedDateString = (type, bound) => {
        const today = new Date();
        const formatDate = (d) => {
            const y = d.getFullYear();
            const m = String(d.getMonth() + 1).padStart(2, '0');
            const day = String(d.getDate()).padStart(2, '0');
            return `${y}-${m}-${day}`;
        };

        if (type === 'today') {
            return formatDate(today);
        } else if (type === 'yesterday') {
            const yesterday = new Date();
            yesterday.setDate(today.getDate() - 1);
            return formatDate(yesterday);
        } else if (type === 'this_week') {
            const dayOfWeek = today.getDay();
            const startOfWeek = new Date(today);
            const diff = today.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1);
            startOfWeek.setDate(diff);
            if (bound === 'start') {
                return formatDate(startOfWeek);
            } else {
                const endOfWeek = new Date(startOfWeek);
                endOfWeek.setDate(startOfWeek.getDate() + 6);
                return formatDate(endOfWeek);
            }
        } else if (type === 'last_week') {
            const dayOfWeek = today.getDay();
            const startOfLastWeek = new Date(today);
            const diff = today.getDate() - dayOfWeek - 6;
            startOfLastWeek.setDate(diff);
            if (bound === 'start') {
                return formatDate(startOfLastWeek);
            } else {
                const endOfLastWeek = new Date(startOfLastWeek);
                endOfLastWeek.setDate(startOfLastWeek.getDate() + 6);
                return formatDate(endOfLastWeek);
            }
        } else if (type === 'this_month') {
            if (bound === 'start') {
                return formatDate(new Date(today.getFullYear(), today.getMonth(), 1));
            } else {
                return formatDate(new Date(today.getFullYear(), today.getMonth() + 1, 0));
            }
        } else if (type === 'prev_month' || type === 'last_month') {
            if (bound === 'start') {
                return formatDate(new Date(today.getFullYear(), today.getMonth() - 1, 1));
            } else {
                return formatDate(new Date(today.getFullYear(), today.getMonth(), 0));
            }
        } else if (type === 'all' || type === 'all_time' || type === 'timeline') {
            if (bound === 'start') {
                return '2000-01-01';
            } else {
                return '2100-01-01';
            }
        }
        return '';
    };

    const handleCustomDateChange = (isStart, val) => {
        setLocalFilterType('custom');
        if (isStart) {
            setLocalStartDate(val);
        } else {
            setLocalEndDate(val);
        }
    };

    useEffect(() => {
        if (showTimelineModal) {
            setLocalFilterType(filterType);
            setLocalStartDate(customStartDate || getCalculatedDateString(filterType, 'start'));
            setLocalEndDate(customEndDate || getCalculatedDateString(filterType, 'end'));
            setLocalStartTime(startTime);
            setLocalEndTime(endTime);
        }
    }, [showTimelineModal, filterType, customStartDate, customEndDate, startTime, endTime]);

    // Helper to format date and time to YYYY-MM-DD hh:mm AM/PM
    const formatDateTime = (dateStr) => {
        if (!dateStr) return '—';
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return dateStr;
        const yyyy = date.getFullYear();
        const mm = String(date.getMonth() + 1).padStart(2, '0');
        const dd = String(date.getDate()).padStart(2, '0');

        let hours = date.getHours();
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const ampm = hours >= 12 ? 'PM' : 'AM';
        hours = hours % 12;
        hours = hours ? hours : 12; // the hour '0' should be '12'
        const strTime = `${yyyy}-${mm}-${dd} ${hours}:${minutes} ${ampm}`;
        return strTime;
    };

    // Helper to format dynamic stage/status names nicely in the UI
    const formatStageName = (code, displayName) => {
        const overrides = {
            'walkin': 'Interview Attended',
            'joined': 'Joined',
            'offer': 'Offered',
            'rejected': 'Rejected',
            'to_client': 'Consider to Client',
            'interview_scheduled': 'Interview Scheduled',
            'call_notpicked': 'Call Not Picked',
            'dis_connect': 'Disconnect',
            'will_revert_back': 'Will Revert Back',
            'newlead': 'Newlead'
        };

        if (code && overrides[code.toLowerCase()]) {
            return overrides[code.toLowerCase()];
        }

        const nameToFormat = displayName || code || '';
        return nameToFormat
            .replace(/[_-]/g, ' ')
            .split(' ')
            .filter(Boolean)
            .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
            .join(' ');
    };

    // Fetch leads from backend
    const fetchLeads = () => {
        setIsLoading(true);
        const params = new URLSearchParams({
            login_emp_id: loginEmpId,
            page: page,
            page_size: pageSize,
            search: search,
            sort_by: sortBy,
            filter_type: filterType
        });
        if (filterType === 'custom') {
            if (customStartDate) params.append('start_date', customStartDate);
            if (customEndDate) params.append('end_date', customEndDate);
        }
        if (startTime) params.append('start_time', startTime);
        if (endTime) params.append('end_time', endTime);

        axios.get(`${port}/root/activity/leads/?${params.toString()}`)
            .then(res => {
                setLeads(res.data.results || []);
                setTotalCount(res.data.total_count || 0);
                setTotalPages(res.data.total_pages || 1);
            })
            .catch(err => {
                console.error("Error fetching leads:", err);
                toast.error("Failed to load leads list.");
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    // Fetch active recruiters for assignment dropdown
    const fetchRecruiters = () => {
        axios.get(`${port}/root/ScreeningAssigning/`)
            .then(res => {
                setRecruiters(res.data || []);
            })
            .catch(err => {
                console.error("Error fetching recruiters:", err);
            });
    };

    // Fetch dynamic stages
    const fetchStages = () => {
        axios.get(`${port}/root/lead-stages/`)
            .then(res => {
                setStages(res.data || []);
            })
            .catch(err => {
                console.error("Error fetching stages:", err);
            });
    };

    // Trigger leads fetch on state changes
    useEffect(() => {
        if (loginEmpId) {
            fetchLeads();
        }
    }, [page, pageSize, search, sortBy, filterType, customStartDate, customEndDate, startTime, endTime, searchParams]);

    // Sync states to URL search parameters
    useEffect(() => {
        const params = new URLSearchParams(searchParams);
        // Update header-level params (preserve cf_ column filters)
        if (page !== 1) params.set('page', page); else params.delete('page');
        if (search) params.set('search', search); else params.delete('search');
        if (sortBy !== '-Created_Date') params.set('sort_by', sortBy); else params.delete('sort_by');
        if (filterType !== 'today') params.set('filter_type', filterType); else params.delete('filter_type');
        if (filterType === 'custom' && customStartDate) params.set('start_date', customStartDate); else params.delete('start_date');
        if (filterType === 'custom' && customEndDate) params.set('end_date', customEndDate); else params.delete('end_date');
        if (startTime) params.set('start_time', startTime); else params.delete('start_time');
        if (endTime) params.set('end_time', endTime); else params.delete('end_time');

        setSearchParams(params, { replace: true });
    }, [page, search, sortBy, filterType, customStartDate, customEndDate, startTime, endTime]);

    // Debounce global search input
    useEffect(() => {
        const handler = setTimeout(() => {
            setSearch(searchInput);
            setPage(1);
        }, 500);
        return () => clearTimeout(handler);
    }, [searchInput]);

    // Sync local input states when search change externally
    useEffect(() => {
        setSearchInput(search);
    }, [search]);

    useEffect(() => {
        fetchRecruiters();
        fetchStages();

        const handleOutsideClick = (e) => {
            if (!e.target.closest('.stage-dropdown-container')) {
                setActiveDropdownLeadId(null);
            }
        };
        document.addEventListener('click', handleOutsideClick);
        return () => document.removeEventListener('click', handleOutsideClick);
    }, []);

    // Selection handlers
    const handleSelectAll = (e) => {
        const currentPageIds = leads.map(lead => lead.id);
        if (e.target.checked) {
            setSelectedLeads(prev => {
                const newSelection = [...prev];
                currentPageIds.forEach(id => {
                    if (!newSelection.includes(id)) {
                        newSelection.push(id);
                    }
                });
                return newSelection;
            });
            setSelectedLeadsData(prev => {
                const newDetails = { ...prev };
                leads.forEach(lead => {
                    newDetails[lead.id] = lead;
                });
                return newDetails;
            });
        } else {
            setSelectedLeads(prev => prev.filter(id => !currentPageIds.includes(id)));
            setSelectedLeadsData(prev => {
                const next = { ...prev };
                currentPageIds.forEach(id => {
                    delete next[id];
                });
                return next;
            });
        }
    };

    const handleSelectLead = (e, id) => {
        const leadObj = leads.find(l => l.id === id);
        if (e.target.checked) {
            setSelectedLeads(prev => [...prev, id]);
            if (leadObj) {
                setSelectedLeadsData(prev => ({ ...prev, [id]: leadObj }));
            }
        } else {
            setSelectedLeads(prev => prev.filter(item => item !== id));
            setSelectedLeadsData(prev => {
                const next = { ...prev };
                delete next[id];
                return next;
            });
        }
    };



    const handleDownloadCSV = () => {
        const headers = ["Name", "Mobile Number", "Position Applied"];
        const rows = selectedLeads.map(id => {
            const lead = selectedLeadsData[id] || {};
            const name = lead.candidate_name || "N/A";
            const phone = lead.candidate_phone || "N/A";
            const designation = lead.candidate_designation || "N/A";
            return [
                `"${name.replace(/"/g, '""')}"`,
                `"${phone}"`,
                `"${designation.replace(/"/g, '""')}"`
            ];
        });

        const csvContent = "data:text/csv;charset=utf-8," 
            + [headers.join(","), ...rows.map(e => e.join(","))].join("\n");
        
        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute("download", `Selected_Leads_${new Date().toISOString().slice(0, 10)}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Bulk assign leads to a recruiter
    const handleBulkAssign = () => {
        if (!selectedLeads.length) {
            toast.warning("Please select at least one lead.");
            return;
        }
        if (!assignTargetRecruiter) {
            toast.warning("Please select a recruiter to assign to.");
            return;
        }

        axios.post(`${port}/root/activity/leads/`, {
            lead_ids: selectedLeads,
            assign_to_emp_id: assignTargetRecruiter,
            login_emp_id: loginEmpId
        })
            .then(res => {
                toast.success(res.data.message || "Successfully reassigned leads.");
                setSelectedLeads([]);
                setSelectedLeadsData({});
                setAssignTargetRecruiter('');
                fetchLeads();
            })
            .catch(err => {
                console.error("Assignment error:", err);
                toast.error(err.response?.data?.error || "Failed to assign leads.");
            });
    };

    // Open Action Modal with lead pre-filled details
    const handleOpenActionModal = (lead) => {
        setSelectedLeadForModal(lead);
        setModalCandidateName(lead.candidate_name || '');
        setModalCandidateEmail(lead.candidate_email || '');
        setModalCandidatePhone(lead.candidate_phone || '');
        setModalCandidateDesignation(lead.candidate_designation || '');
        setModalSource(lead.source || '');
        setModalRemarks(lead.interview_call_remarks || '');
        setModalPriority(leadPriorities[lead.id] || 'medium');
        setModalRecruiter(lead.handled_by_id || '');
        setShowActionModal(true);
    };

    // Save Action Modal changes (updates details, priority, remarks, assignment)
    const handleSaveModalChanges = async () => {
        if (!selectedLeadForModal) return;

        if (!modalRemarks || !modalRemarks.trim()) {
            toast.warning("Remarks/comments are mandatory.");
            return;
        }

        setIsSavingModal(true);
        try {
            const patchData = {};
            if (modalCandidateName !== (selectedLeadForModal.candidate_name || '')) {
                patchData.candidate_name = modalCandidateName;
            }
            if (modalCandidateEmail !== (selectedLeadForModal.candidate_email || '')) {
                patchData.candidate_email = modalCandidateEmail;
            }
            if (modalCandidatePhone !== (selectedLeadForModal.candidate_phone || '')) {
                patchData.candidate_phone = modalCandidatePhone;
            }
            if (modalCandidateDesignation !== (selectedLeadForModal.candidate_designation || '')) {
                patchData.candidate_designation = modalCandidateDesignation;
            }
            if (modalSource !== (selectedLeadForModal.source || '')) {
                patchData.source = modalSource;
            }
            if (modalRemarks !== (selectedLeadForModal.interview_call_remarks || '')) {
                patchData.interview_call_remarks = modalRemarks;
            }

            // Save Details & Remarks if changed
            if (Object.keys(patchData).length > 0) {
                await axios.patch(`${port}/root/create-daily-achieved-activity/${selectedLeadForModal.id}`, patchData);
            }

            // Save Priority in local UI state
            setLeadPriorities(prev => ({ ...prev, [selectedLeadForModal.id]: modalPriority }));

            // Save Recruiter reassignment if changed (HR / Admins or permitted users)
            if (canAssignLeads && modalRecruiter && modalRecruiter !== (selectedLeadForModal.handled_by_id || '')) {
                await axios.post(`${port}/root/activity/leads/`, {
                    lead_ids: [selectedLeadForModal.id],
                    assign_to_emp_id: modalRecruiter,
                    login_emp_id: loginEmpId
                });
            }

            toast.success("Lead updated successfully.");
            setShowActionModal(false);
            fetchLeads();
        } catch (err) {
            console.error("Save lead changes error:", err);
            toast.error("Failed to save changes.");
        } finally {
            setIsSavingModal(false);
        }
    };

    // Inline Stage/Status Change Handlers
    const handleStageSelect = (lead, newStageCode) => {
        const currentStage = lead.lead_stage || lead.interview_status || lead.client_status || '';
        setReviewLead(lead);
        setReviewNewStage(newStageCode);
        setReviewCurrentStatus('');
        setReviewRemarks(lead.interview_call_remarks || lead.client_call_remarks || '');
        setReviewScheduleFollowup(false);
        setReviewFollowupDate(lead.next_followup_date || '');
        setReviewFollowupTime(lead.next_followup_time || '');
        setReviewFollowupNotes(lead.next_followup_notes || '');

        let defaultInterviewDate = '';
        if (lead.interview_scheduled_date) {
            defaultInterviewDate = lead.interview_scheduled_date.includes('T')
                ? lead.interview_scheduled_date.slice(0, 16)
                : lead.interview_scheduled_date;
        }
        setReviewInterviewDate(defaultInterviewDate);
        setReviewScheduleFollowup(false);
        setReviewIsInterview(false);
        setShowReviewModal(true);
    };

    const handleSaveReview = async () => {
        if (!reviewLead || !reviewNewStage) return;

        const isClient = !!(reviewLead.client_name || reviewLead.client_phone || reviewLead.client_company_name);

        if (SUB_STATUSES[reviewNewStage] && !reviewCurrentStatus) {
            toast.warning(`Please select a status for the ${formatStageName(reviewNewStage)} stage.`);
            return;
        }

        if (!reviewRemarks || !reviewRemarks.trim()) {
            toast.warning("Remarks/comments are mandatory for stage updates.");
            return;
        }

        setIsSavingReview(true);
        try {
            const patchData = {
                lead_stage: reviewNewStage,
                current_status: reviewCurrentStatus,
            };

            if (isClient) {
                patchData.client_call_remarks = reviewRemarks;
                if (reviewNewStage === 'follow_up') {
                    patchData.client_status = 'followup';
                } else if (reviewNewStage === 'consider_to_client') {
                    patchData.client_status = 'converted_to_client';
                } else if (reviewNewStage === 'closed') {
                    patchData.client_status = 'closed';
                } else if (reviewNewStage === 'prospect') {
                    patchData.client_status = 'prospect';
                }
            } else {
                patchData.interview_call_remarks = reviewRemarks;
                if (reviewNewStage === 'follow_up') {
                    if (!['interview_scheduled', 'interview_attended'].includes(reviewCurrentStatus)) {
                        patchData.interview_status = reviewCurrentStatus;
                    }
                } else if (reviewNewStage === 'interview_scheduled') {
                    patchData.interview_status = 'interview_scheduled';
                } else if (reviewNewStage === 'interview_attended') {
                    patchData.interview_status = 'walkin';
                } else if (reviewNewStage === 'consider_to_client') {
                    if (reviewCurrentStatus === 'offered') patchData.interview_status = 'offer';
                    else if (reviewCurrentStatus === 'joined') patchData.interview_status = 'joined';
                    else if (reviewCurrentStatus === 'rejected') patchData.interview_status = 'rejected';
                    else if (reviewCurrentStatus === 'rejected_by_candidate') patchData.interview_status = 'Rejected_by_Candidate';
                } else if (reviewNewStage === 'internal_hired') {
                    if (reviewCurrentStatus === 'offered') patchData.interview_status = 'offer';
                    else if (reviewCurrentStatus === 'joined') patchData.interview_status = 'joined';
                    else if (reviewCurrentStatus === 'rejected') patchData.interview_status = 'rejected';
                    else if (reviewCurrentStatus === 'rejected_by_candidate') patchData.interview_status = 'Rejected_by_Candidate';
                } else if (reviewNewStage === 'closed') {
                    patchData.interview_status = 'rejected';
                }
            }

            if (reviewNewStage === 'interview_attended' || reviewCurrentStatus === 'interview_attended') {
                patchData.interview_walkin_date = new Date().toISOString();
                patchData.interview_attendance = 'Attended';
                patchData.interview_status = 'walkin';
            }
            if (reviewNewStage === 'interview_scheduled' || reviewCurrentStatus === 'interview_scheduled') {
                if (!reviewInterviewDate) {
                    toast.warning("Please fill in the interview scheduled date.");
                    setIsSavingReview(false);
                    return;
                }
                patchData.interview_scheduled_date = new Date(reviewInterviewDate).toISOString();
                patchData.interview_attendance = 'Scheduled';
                patchData.interview_status = 'interview_scheduled';
            }

            // Save status & remarks
            await axios.patch(`${port}/root/create-daily-achieved-activity/${reviewLead.id}`, patchData);

            // Save follow-up if checked
            if (reviewScheduleFollowup) {
                if (!reviewFollowupDate || !reviewFollowupTime) {
                    toast.warning("Please fill in the expected date and time for scheduling the follow-up.");
                    setIsSavingReview(false);
                    return;
                }
                await axios.post(`${port}/root/activity/convert-to-followup`, {
                    activity_record_id: reviewLead.id,
                    expected_date: reviewFollowupDate,
                    expected_time: reviewFollowupTime,
                    notes: reviewFollowupNotes,
                    login_emp_id: loginEmpId,
                    is_interview: false
                });
            }

            toast.success(`Stage updated to ${formatStageName(reviewNewStage)}.`);
            setShowReviewModal(false);
            setReviewLead(null);
            fetchLeads();
        } catch (err) {
            console.error("Save stage review error:", err);
            toast.error("Failed to update status.");
        } finally {
            setIsSavingReview(false);
        }
    };

    // Inline Dedicated Follow-Up Handlers
    const handleOpenFollowupModal = (lead) => {
        setFollowupLead(lead);
        setFollowupDate(lead.next_followup_date || '');
        setFollowupTime(lead.next_followup_time || '');
        setFollowupNotes(lead.next_followup_notes || '');
        setFollowupIsInterview(lead.interview_attendance === 'Scheduled');
        setShowFollowupModal(true);
    };

    const handleSaveFollowup = async () => {
        if (!followupLead) return;
        if (!followupDate || !followupTime) {
            toast.warning("Please fill in the expected date and time.");
            return;
        }
        if (!followupNotes || !followupNotes.trim()) {
            toast.warning("Follow-up remarks/notes are mandatory.");
            return;
        }

        setIsSavingFollowup(true);
        try {
            await axios.post(`${port}/root/activity/convert-to-followup`, {
                activity_record_id: followupLead.id,
                expected_date: followupDate,
                expected_time: followupTime,
                notes: followupNotes,
                login_emp_id: loginEmpId,
                is_interview: followupIsInterview
            });

            toast.success("Follow-up scheduled successfully.");
            setShowFollowupModal(false);
            setFollowupLead(null);
            fetchLeads();
        } catch (err) {
            console.error("Save follow-up error:", err);
            toast.error("Failed to schedule follow-up.");
        } finally {
            setIsSavingFollowup(false);
        }
    };

    // Excel/CSV Bulk Upload handler
    const handleFileUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setUploading(true);
        const formData = new FormData();
        formData.append("file", file);

        axios.post(`${port}/root/bulk-activity-upload?activity_list_id=1&login_emp_id=${loginEmpId}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        })
            .then(res => {
                toast.success(res.data.message || "Bulk upload successful!");
                fetchLeads();
            })
            .catch(err => {
                console.error("Upload error:", err);
                const errMsg = err.response?.data?.error || "Bulk upload failed.";
                toast.error(errMsg);
            })
            .finally(() => {
                setUploading(false);
                e.target.value = null; // reset file input
            });
    };

    // Helper functions for badge colors
    const getLeadBadgeLabel = (lead) => {
        if (lead.lead_stage) {
            const stageName = formatStageName(lead.lead_stage);
            if (lead.current_status) {
                return `${stageName} (${formatStageName(lead.current_status)})`;
            }
            return stageName;
        }
        const legacyStatus = lead.interview_status || lead.client_status;
        return legacyStatus ? formatStageName(legacyStatus) : 'Newlead';
    };

    const getLeadBadgeClass = (lead) => {
        const stage = lead.lead_stage || lead.interview_status || lead.client_status || '';
        const subStatus = lead.current_status || '';
        const checkValue = (subStatus || stage).toLowerCase();

        switch (checkValue) {
            case 'joined':
                return 'bg-transparent text-green-600 border-green-500';
            case 'offer':
            case 'offered':
                return 'bg-transparent text-blue-600 border-blue-500';
            case 'interview_scheduled':
                return 'bg-transparent text-purple-600 border-purple-500';
            case 'walkin':
            case 'interview_attended':
                return 'bg-transparent text-amber-600 border-amber-500';
            case 'rejected':
            case 'rejected_by_candidate':
                return 'bg-transparent text-red-600 border-red-500';
            case 'call_notpicked':
            case 'dis_connect':
            case 'disconnect':
            case 'will_revert_back':
            case 'follow_up':
                return 'bg-transparent text-slate-600 border-slate-400';
            default:
                return 'bg-transparent text-indigo-600 border-indigo-500';
        }
    };

    const getStatusBadgeClass = (status) => {
        return getLeadBadgeClass({ interview_status: status });
    };

    const getStageLabel = (lead) => {
        const stage = lead.lead_stage || lead.interview_status || lead.client_status;
        return stage ? formatStageName(stage) : 'Newlead';
    };

    const getStageClass = (lead) => {
        const stage = lead.lead_stage || lead.interview_status || lead.client_status || '';
        const checkValue = stage.toLowerCase();
        switch (checkValue) {
            case 'interview_scheduled':
                return 'bg-transparent text-purple-600 border-purple-500';
            case 'walkin':
            case 'interview_attended':
                return 'bg-transparent text-amber-600 border-amber-500';
            case 'closed':
                return 'bg-transparent text-rose-600 border-rose-500';
            case 'follow_up':
                return 'bg-transparent text-slate-600 border-slate-400';
            case 'consider_to_client':
            case 'internal_hired':
                return 'bg-transparent text-indigo-600 border-indigo-500';
            default:
                return 'bg-transparent text-indigo-600 border-indigo-500';
        }
    };

    const getStatusLabel = (lead) => {
        if (lead.lead_stage) {
            // For follow_up stage, also show interview_scheduled and interview_attended as sub-status
            if (lead.lead_stage === 'follow_up' && lead.interview_status) {
                const interviewSubStatuses = ['interview_scheduled', 'walkin', 'interview_attended'];
                if (interviewSubStatuses.includes(lead.interview_status.toLowerCase())) {
                    return formatStageName(lead.interview_status);
                }
            }
            return lead.current_status ? formatStageName(lead.current_status) : '—';
        }
        const status = lead.interview_status || lead.client_status;
        if (!status) return '—';
        const isMainStage = ['interview_scheduled', 'walkin', 'interview_attended', 'closed', 'newlead'].includes(status.toLowerCase());
        if (isMainStage) return '—';
        return formatStageName(status);
    };

    const getStatusClass = (lead) => {
        const statusLabel = getStatusLabel(lead);
        if (statusLabel === '—') return 'text-slate-400 font-medium';
        const checkValue = (lead.current_status || lead.interview_status || lead.client_status || '').toLowerCase();
        switch (checkValue) {
            case 'joined':
                return 'bg-transparent text-green-600 border-green-500';
            case 'offer':
            case 'offered':
                return 'bg-transparent text-blue-600 border-blue-500';
            case 'interview_scheduled':
                return 'bg-transparent text-purple-600 border-purple-500';
            case 'walkin':
            case 'interview_attended':
                return 'bg-transparent text-emerald-600 border-emerald-500';
            case 'rejected':
            case 'rejected_by_candidate':
                return 'bg-transparent text-red-600 border-red-500';
            case 'call_notpicked':
            case 'dis_connect':
            case 'disconnect':
            case 'will_revert_back':
                return 'bg-transparent text-slate-600 border-slate-400';
            default:
                return 'bg-transparent text-slate-600 border-slate-400';
        }
    };

    const getPriorityBadgeClass = (priority) => {
        switch (priority?.toLowerCase()) {
            case 'high':
                return 'bg-transparent text-red-600 border-red-500';
            case 'medium':
                return 'bg-transparent text-amber-600 border-amber-500';
            case 'low':
                return 'bg-transparent text-slate-500 border-slate-400';
            default:
                return 'bg-transparent text-slate-500 border-slate-400';
        }
    };

    const filteredLeads = leads.filter(lead => {
        return Object.entries(colFilters).every(([key, val]) => {
            if (!val) return true;

            if (key === 'id') {
                return String(lead.id).toLowerCase().includes(val.toLowerCase());
            }
            if (key === 'candidate_name') {
                const name = lead.candidate_name || lead.client_name || lead.candidate_or_client_name || '';
                return name.toLowerCase().includes(val.toLowerCase());
            }
            if (key === 'candidate_email') {
                const email = lead.candidate_email || lead.client_email || '';
                return email.toLowerCase().includes(val.toLowerCase());
            }
            if (key === 'candidate_phone') {
                const phone = lead.candidate_phone || lead.client_phone || lead.candidate_or_client_phone || '';
                return phone.toLowerCase().includes(val.toLowerCase());
            }
            if (key === 'source') {
                const src = lead.source || lead.client_company_name || '';
                return src.toLowerCase().includes(val.toLowerCase());
            }
            if (key === 'candidate_designation') {
                const desg = lead.candidate_designation || lead.client_enquire_purpose || lead.position || '';
                return desg.toLowerCase().includes(val.toLowerCase());
            }
            if (key === 'lead_stage') {
                const stage = lead.lead_stage || lead.interview_status || lead.client_status || '';
                return stage.toLowerCase() === val.toLowerCase();
            }
            if (key === 'current_status') {
                const status = lead.current_status || lead.interview_status || lead.client_status || '';
                return status.toLowerCase().includes(val.toLowerCase());
            }
            if (key === 'interview_status') {
                const status = lead.interview_status || lead.client_status || '';
                return status.toLowerCase() === val.toLowerCase();
            }
            if (key === 'interview_attendance') {
                const att = lead.interview_attendance || '';
                return att.toLowerCase() === val.toLowerCase();
            }
            if (key === 'interview_scheduled_date') {
                const isd = lead.interview_scheduled_date ? formatDateTime(lead.interview_scheduled_date).toLowerCase() : '';
                return isd.includes(val.toLowerCase());
            }
            if (key === 'handled_by') {
                const hb = lead.handled_by || '';
                return hb.toLowerCase().includes(val.toLowerCase());
            }
            if (key === 'assigned_by') {
                const ab = lead.assigned_by || '';
                return ab.toLowerCase().includes(val.toLowerCase());
            }
            if (key === 'interview_call_remarks') {
                const rem = lead.interview_call_remarks || '';
                return rem.toLowerCase().includes(val.toLowerCase());
            }
            if (key === 'Created_Date') {
                const dateStr = formatDateTime(lead.Created_Date).toLowerCase();
                return dateStr.includes(val.toLowerCase());
            }
            if (key === 'next_followup') {
                const fdate = lead.next_followup_date ? new Date(lead.next_followup_date).toLocaleDateString('en-GB').replace(/\//g, '-') : '';
                return fdate.toLowerCase().includes(val.toLowerCase());
            }

                return true;
        });
    });

    return (
        <div className="container-fluid p-4 font-popins text-slate-800 poppins">
            {/* Top Header Card */}
            <div className="bg-blue-600 text-white rounded-xl px-4 py-3 flex justify-between items-center shadow-sm">
                <h5 className="font-bold mb-0 text-sm md:text-base flex items-center gap-3">
                    <button
                        onClick={() => navigate('/activity')}
                        className="bg-transparent border-0 text-white p-0 hover:text-slate-200 transition-colors cursor-pointer flex items-center justify-center"
                        title="Back to Dashboard"
                    >
                        <i className="fas fa-arrow-left text-base"></i>
                    </button>
                    Incoming Candidate Leads
                </h5>

                <div className="flex items-center gap-2">
                    {/* Download Template Action Button */}
                    <a
                        href={`${port}/root/download-activity-template?activity_list_id=1`}
                        title="Download Template"
                        className="w-8 h-8 rounded-lg bg-green-600 hover:bg-green-700 text-white flex justify-center items-center transition-all"
                        download
                    >
                        <i className="fas fa-cloud-download-alt text-sm"></i>
                    </a>

                    {/* Bulk Upload Leads Action */}
                    <label
                        title="Bulk Upload Leads"
                        className="w-8 h-8 rounded-lg bg-slate-700 hover:bg-slate-800 text-white flex justify-center items-center transition-all cursor-pointer mb-0"
                    >
                        <i className="fas fa-upload text-sm"></i>
                        <input
                            type="file"
                            accept=".csv, .xlsx, .xls"
                            className="hidden"
                            onChange={handleFileUpload}
                            disabled={uploading}
                        />
                    </label>
                </div>
            </div>

            {/* Filter controls subbar */}
            <div className="flex justify-between items-center my-3 px-1 flex-wrap gap-2">
                <div className="flex items-center gap-3">
                    {/* Global Search Input */}
                    <div className="flex flex-col relative">
                        <div className="flex justify-between items-center mb-1">
                            <span className="text-[12px] poppins text-slate-500 font-semibold uppercase tracking-wider">Search Leads</span>
                            <OverlayTrigger
                                placement="top"
                                delay={{ show: 150, hide: 250 }}
                                overlay={
                                    <Tooltip id="search-tooltip" className="shadow-lg">
                                        <div className="text-center text-xs p-1">
                                            Search by Candidate name, Candidate id, Email, Mobile number, Source, Position applied, Current status, Handled by, Assigned by, Remarks, Next Follow-up.
                                        </div>
                                    </Tooltip>
                                }
                            >
                                <span className="cursor-pointer text-slate-400 hover:text-slate-600 flex items-center justify-center">
                                    <i className="fas fa-info-circle text-[11px]"></i>
                                </span>
                            </OverlayTrigger>
                        </div>
                        <div className="flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-2.5 py-1.5 shadow-sm w-[280px]">
                            <i className="fas fa-search text-slate-400 text-xs"></i>
                            <input
                                type="text"
                                placeholder="Search..."
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                className="border-0 bg-transparent text-xs text-slate-700 outline-none focus:ring-0 p-0 w-full placeholder:text-slate-400"
                            />
                            {searchInput && (
                                <button
                                    onClick={() => { setSearchInput(''); setSearch(''); }}
                                    className="border-0 bg-transparent p-0 text-slate-400 hover:text-slate-600 flex items-center justify-center"
                                >
                                    <i className="fas fa-times-circle text-xs"></i>
                                </button>
                            )}
                        </div>
                    </div>

                    {canAssignLeads && selectedLeads.length > 0 && (
                        <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-2 py-1 shadow-sm">
                            <input
                                type="checkbox"
                                checked={true}
                                onChange={() => {
                                    setSelectedLeads([]);
                                    setSelectedLeadsData({});
                                }}
                                className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                            />
                            <span className="text-xs font-semibold text-slate-600">
                                Selected ({selectedLeads.length}) leads
                            </span>
                        </div>
                    )}
                </div>

                <div className="flex items-center gap-2">
                    {selectedLeads.length > 0 && (
                        <>
                            <button
                                onClick={handleDownloadCSV}
                                className="flex items-center gap-1.5 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-lg text-xs shadow-sm transition-all"
                                title="Download CSV"
                            >
                                <i className="fas fa-download text-[11px]"></i> CSV
                            </button>
                            <button
                                onClick={() => setShowWAModal(true)}
                                className="flex items-center gap-1.5 px-3 py-1.5 text-white font-semibold rounded-lg text-xs shadow-sm transition-all"
                                style={{ backgroundColor: '#25D366' }}
                                title="Send WhatsApp Message"
                            >
                                <i className="fab fa-whatsapp text-[13px]"></i> Send WhatsApp
                            </button>
                        </>
                    )}
                    {canAssignLeads && (
                        <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-lg px-2 py-1 shadow-sm">
                            <span className="text-sm text-slate-500 font-medium">Assign to:</span>
                            <select
                                value={assignTargetRecruiter}
                                onChange={(e) => setAssignTargetRecruiter(e.target.value)}
                                className="border-0 bg-transparent text-xs text-slate-700 outline-none focus:ring-0 py-0"
                            >
                                <option value="">Select Employee</option>
                                {recruiters.map((rec) => (
                                    <option key={rec.id} value={rec.EmployeeId}>
                                        {rec.Name}
                                    </option>
                                ))}
                            </select>
                            <button
                                onClick={handleBulkAssign}
                                disabled={!selectedLeads.length || !assignTargetRecruiter}
                                className="px-2 py-0.5 bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-100 disabled:text-slate-400 text-white font-semibold rounded text-[10px] transition-all"
                            >
                                Assign
                            </button>
                        </div>
                    )}

                    {/* Timeline Filter Toggle Button */}
                    <button
                        onClick={() => setShowTimelineModal(true)}
                        className="flex items-center gap-1 bg-white border border-slate-200 rounded-lg px-2 py-1 shadow-sm hover:border-slate-300 transition-all duration-150 focus:outline-none"
                        title="Timeline Selection"
                    >
                        <i className="fas fa-sliders-h text-indigo-600 text-xs"></i>
                        <span className="text-sm text-slate-500 font-medium poppins">Timeline:</span>
                        <span className="text-xs font-semibold text-indigo-700">{getFilterTypeDisplayName()}</span>
                    </button>

                    <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-lg px-2 py-1 shadow-sm">
                        <span className="text-sm text-slate-500 font-medium">Sort By:</span>
                        <select
                            value={sortBy}
                            onChange={(e) => { setSortBy(e.target.value); setPage(1); }}
                            className="border-0 bg-transparent text-xs text-slate-700 outline-none focus:ring-0 py-0"
                        >
                            <option value="-Created_Date">Newest First</option>
                            <option value="Created_Date">Oldest First</option>
                            <option value="candidate_name">Name A-Z</option>
                            <option value="-candidate_name">Name Z-A</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Table */}
            <main className="table-responsive tablebg h-[60vh] overflow-x-auto sm:h-[65vh] md:h-[70vh] lg:h-[70vh] xl:h-[70vh]">
                {isLoading ? (
                    <LocalLoader />
                ) : (
                    <table className="w-full table-fixed min-w-[2000px] border-collapse border border-slate-200">
                        <thead>
                            {/* Column Titles Row */}
                            <tr className="bg-slate-100 text-slate-800 font-semibold text-sm whitespace-nowrap">
                                <th style={{ width: '3%' }} className="p-2 text-center border border-slate-200">
                                    <input
                                        type="checkbox"
                                        onChange={handleSelectAll}
                                        checked={leads.length > 0 && leads.every(lead => selectedLeads.includes(lead.id))}
                                        className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                                    />
                                </th>
                                <th style={{ width: '4%' }} className="p-2 text-center border border-slate-200">SI No</th>
                                <th style={{ width: '10%' }} className="p-2 text-center border border-slate-200">Name</th>
                                <th style={{ width: '7%' }} className="p-2 text-center border border-slate-200">Cand. ID</th>
                                <th style={{ width: '15%' }} className="p-2 text-center border border-slate-200">Email ID</th>
                                <th style={{ width: '12%' }} className="p-2 text-center border border-slate-200">Mobile Number</th>
                                <th style={{ width: '16%' }} className="p-2 text-center border border-slate-200">Added Date/Time</th>
                                <th style={{ width: '8%' }} className="p-2 text-center border border-slate-200">Source</th>
                                <th style={{ width: '12%' }} className="p-2 text-center border border-slate-200">Position Applied</th>
                                <th style={{ width: '14%' }} className="p-2 text-center border border-slate-200">Lead Stage</th>
                                <th style={{ width: '12%' }} className="p-2 text-center border border-slate-200">Current Status</th>
                                <th style={{ width: '15%' }} className="p-2 text-center border border-slate-200">Interview Status</th>
                                <th style={{ width: '12%' }} className="p-2 text-center border border-slate-200">Interview Date</th>
                                <th style={{ width: '12%' }} className="p-2 text-center border border-slate-200">Handled By</th>
                                <th style={{ width: '12%' }} className="p-2 text-center border border-slate-200">Assigned By</th>
                                <th style={{ width: '12%' }} className="p-2 text-center border border-slate-200">Remarks</th>
                                <th style={{ width: '12%' }} className="p-2 text-center border border-slate-200">Next Follow-up</th>
                                <th style={{ width: '7%' }} className="p-2 text-center border border-slate-200">Action</th>
                                {/* <th style={{ width: '6%' }} className="p-2 text-center border border-slate-200">Details</th> */}
                            </tr>

                            {/* Column Filters Row */}
                            <tr className="bg-slate-50 text-slate-700 font-normal text-xs whitespace-nowrap">
                                <th className="p-1.5 text-center border border-slate-200">
                                    {isAnyColFilterActive && (
                                        <button
                                            onClick={clearColFilters}
                                            title="Clear column filters"
                                            className="text-red-500 hover:text-red-700 font-bold focus:outline-none flex items-center justify-center mx-auto"
                                        >
                                            <i className="fas fa-filter-circle-xmark text-sm"></i>
                                        </button>
                                    )}
                                </th>
                                <th className="p-1.5 text-center border border-slate-200">
                                    <span className="text-slate-400 font-medium">—</span>
                                </th>
                                <th className="p-1.5 text-center border border-slate-200">
                                    <input
                                        type="text"
                                        placeholder="Filter Name"
                                        value={colFilters.candidate_name}
                                        onChange={(e) => setColFilters(prev => ({ ...prev, candidate_name: e.target.value }))}
                                        className="w-full px-2 py-1 text-xs border border-slate-300 rounded outline-none focus:border-indigo-500 bg-white text-slate-700 font-normal placeholder:text-slate-400 focus:ring-1 focus:ring-indigo-500"
                                    />
                                </th>
                                <th className="p-1.5 text-center border border-slate-200">
                                    <input
                                        type="text"
                                        placeholder="ID"
                                        value={colFilters.id}
                                        onChange={(e) => setColFilters(prev => ({ ...prev, id: e.target.value }))}
                                        className="w-full px-2 py-1 text-xs border border-slate-300 rounded outline-none focus:border-indigo-500 bg-white text-slate-700 font-normal placeholder:text-slate-400 focus:ring-1 focus:ring-indigo-500"
                                    />
                                </th>
                                <th className="p-1.5 text-center border border-slate-200">
                                    <input
                                        type="text"
                                        placeholder="Filter Email"
                                        value={colFilters.candidate_email}
                                        onChange={(e) => setColFilters(prev => ({ ...prev, candidate_email: e.target.value }))}
                                        className="w-full px-2 py-1 text-xs border border-slate-300 rounded outline-none focus:border-indigo-500 bg-white text-slate-700 font-normal placeholder:text-slate-400 focus:ring-1 focus:ring-indigo-500"
                                    />
                                </th>
                                <th className="p-1.5 text-center border border-slate-200">
                                    <input
                                        type="text"
                                        placeholder="Filter Contact"
                                        value={colFilters.candidate_phone}
                                        onChange={(e) => setColFilters(prev => ({ ...prev, candidate_phone: e.target.value }))}
                                        className="w-full px-2 py-1 text-xs border border-slate-300 rounded outline-none focus:border-indigo-500 bg-white text-slate-700 font-normal placeholder:text-slate-400 focus:ring-1 focus:ring-indigo-500"
                                    />
                                </th>
                                <th className="p-1.5 text-center border border-slate-200">
                                    <input
                                        type="text"
                                        placeholder="Filter Date"
                                        value={colFilters.Created_Date}
                                        onChange={(e) => setColFilters(prev => ({ ...prev, Created_Date: e.target.value }))}
                                        className="w-full px-2 py-1 text-xs border border-slate-300 rounded outline-none focus:border-indigo-500 bg-white text-slate-700 font-normal placeholder:text-slate-400 focus:ring-1 focus:ring-indigo-500"
                                    />
                                </th>
                                <th className="p-1.5 text-center border border-slate-200">
                                    <input
                                        type="text"
                                        placeholder="Filter Source"
                                        value={colFilters.source}
                                        onChange={(e) => setColFilters(prev => ({ ...prev, source: e.target.value }))}
                                        className="w-full px-2 py-1 text-xs border border-slate-300 rounded outline-none focus:border-indigo-500 bg-white text-slate-700 font-normal placeholder:text-slate-400 focus:ring-1 focus:ring-indigo-500"
                                    />
                                </th>
                                <th className="p-1.5 text-center border border-slate-200">
                                    <input
                                        type="text"
                                        placeholder="Filter Position"
                                        value={colFilters.candidate_designation}
                                        onChange={(e) => setColFilters(prev => ({ ...prev, candidate_designation: e.target.value }))}
                                        className="w-full px-2 py-1 text-xs border border-slate-300 rounded outline-none focus:border-indigo-500 bg-white text-slate-700 font-normal placeholder:text-slate-400 focus:ring-1 focus:ring-indigo-500"
                                    />
                                </th>
                                <th className="p-1.5 text-center border border-slate-200">
                                    <select
                                        value={colFilters.lead_stage || ''}
                                        onChange={(e) => setColFilters(prev => ({ ...prev, lead_stage: e.target.value }))}
                                        className="w-full px-1 py-1 text-xs border border-slate-300 rounded outline-none focus:border-indigo-500 bg-white text-slate-700 font-normal cursor-pointer focus:ring-1 focus:ring-indigo-500"
                                    >
                                        <option value="">Stage</option>
                                        <option value="newlead">New Lead</option>
                                        {INTERVIEW_STAGES.map((stage) => (
                                            <option key={stage.code} value={stage.code}>
                                                {stage.display_name}
                                            </option>
                                        ))}
                                    </select>
                                </th>
                                <th className="p-1.5 text-center border border-slate-200">
                                    <input
                                        type="text"
                                        placeholder="Filter Status"
                                        value={colFilters.current_status || ''}
                                        onChange={(e) => setColFilters(prev => ({ ...prev, current_status: e.target.value }))}
                                        className="w-full px-2 py-1 text-xs border border-slate-300 rounded outline-none focus:border-indigo-500 bg-white text-slate-700 font-normal placeholder:text-slate-400 focus:ring-1 focus:ring-indigo-500"
                                    />
                                </th>
                                <th className="p-1.5 text-center border border-slate-200">
                                    <select
                                        value={colFilters.interview_attendance || ''}
                                        onChange={(e) => setColFilters(prev => ({ ...prev, interview_attendance: e.target.value }))}
                                        className="w-full px-1 py-1 text-xs border border-slate-300 rounded outline-none focus:border-indigo-500 bg-white text-slate-700 font-normal cursor-pointer focus:ring-1 focus:ring-indigo-500"
                                    >
                                        <option value="">Status</option>
                                        <option value="Scheduled">Scheduled</option>
                                        <option value="Attended">Attended</option>
                                    </select>
                                </th>
                                <th className="p-1.5 text-center border border-slate-200">
                                    <input
                                        type="text"
                                        placeholder="Filter Interview Date"
                                        value={colFilters.interview_scheduled_date || ''}
                                        onChange={(e) => setColFilters(prev => ({ ...prev, interview_scheduled_date: e.target.value }))}
                                        className="w-full px-2 py-1 text-xs border border-slate-300 rounded outline-none focus:border-indigo-500 bg-white text-slate-500 font-normal placeholder:text-slate-400 focus:ring-1 focus:ring-indigo-500"
                                    />
                                </th>
                                <th className="p-1.5 text-center border border-slate-200">
                                    <input
                                        type="text"
                                        placeholder="Filter Recruiter"
                                        value={colFilters.handled_by}
                                        onChange={(e) => setColFilters(prev => ({ ...prev, handled_by: e.target.value }))}
                                        className="w-full px-2 py-1 text-xs border border-slate-300 rounded outline-none focus:border-indigo-500 bg-white text-slate-700 font-normal placeholder:text-slate-400 focus:ring-1 focus:ring-indigo-500"
                                    />
                                </th>
                                <th className="p-1.5 text-center border border-slate-200">
                                    <input
                                        type="text"
                                        placeholder="Filter Assigner"
                                        value={colFilters.assigned_by}
                                        onChange={(e) => setColFilters(prev => ({ ...prev, assigned_by: e.target.value }))}
                                        className="w-full px-2 py-1 text-xs border border-slate-300 rounded outline-none focus:border-indigo-500 bg-white text-slate-700 font-normal placeholder:text-slate-400 focus:ring-1 focus:ring-indigo-500"
                                    />
                                </th>
                                <th className="p-1.5 text-center border border-slate-200">
                                    <input
                                        type="text"
                                        placeholder="Filter Remarks"
                                        value={colFilters.interview_call_remarks}
                                        onChange={(e) => setColFilters(prev => ({ ...prev, interview_call_remarks: e.target.value }))}
                                        className="w-full px-2 py-1 text-xs border border-slate-300 rounded outline-none focus:border-indigo-500 bg-white text-slate-700 font-normal placeholder:text-slate-400 focus:ring-1 focus:ring-indigo-500"
                                    />
                                </th>
                                <th className="p-1.5 text-center border border-slate-200">
                                    <input
                                        type="text"
                                        placeholder="Filter Date"
                                        value={colFilters.next_followup}
                                        onChange={(e) => setColFilters(prev => ({ ...prev, next_followup: e.target.value }))}
                                        className="w-full px-2 py-1 text-xs border border-slate-300 rounded outline-none focus:border-indigo-500 bg-white text-slate-700 font-normal placeholder:text-slate-400 focus:ring-1 focus:ring-indigo-500"
                                    />
                                </th>
                                <th className="p-1.5 text-center border border-slate-200">
                                    <span className="text-slate-400 font-medium">—</span>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredLeads.length === 0 ? (
                                <tr>
                                    <td colSpan="18" className="text-center py-8 italic text-slate-400 border border-slate-200 text-xs">
                                        No incoming leads found matching the filters.
                                    </td>
                                </tr>
                            ) : (
                                filteredLeads.map((lead, index) => {
                                    const currentPriority = leadPriorities[lead.id] || 'medium';
                                    const dropdownPlacementClass = (index >= filteredLeads.length - 4 && filteredLeads.length > 4)
                                        ? 'bottom-full mb-1 origin-bottom-right'
                                        : 'top-full mt-1 origin-top-right';

                                    return (
                                        <tr key={lead.id} className="border-b hover:bg-slate-100">

                                            {/* Selection Box */}
                                            <td className="p-2 text-center border border-slate-200 text-sm">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedLeads.includes(lead.id)}
                                                    onChange={(e) => handleSelectLead(e, lead.id)}
                                                    className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                                                />
                                            </td>
                                            {/* SI No */}
                                            <td className="p-2 text-center border border-slate-200 text-sm">
                                                {index + 1 + (page - 1) * pageSize}
                                            </td>

                                            {/* Full Name */}
                                            {/* <td className="p-2 text-center font-bold text-slate-800 border border-slate-200 text-sm">{lead.candidate_name || 'N/A'}</td> */}
                                            <td className="p-2 text-center font-bold text-slate-800 border border-slate-200 text-sm"><a onClick={() => navigate(`/activity/lead-log/${lead.id}`)}
                                                className=" font-semibold hover:text-blue-700 cursor-pointer transition-all">{lead.candidate_name || 'N/A'}</a>

                                            </td>

                                            {/* Candidate ID */}
                                            <td className="p-2 text-center border border-slate-200 text-sm">{lead.id}</td>

                                            {/* Email ID */}
                                            <td className="p-2 text-center break-all border border-slate-200 text-sm">{lead.candidate_email || '—'}</td>

                                            {/* Contact Number */}
                                            <td className="p-2 text-center border border-slate-200 text-sm">
                                                {lead.candidate_phone ? (
                                                    <a
                                                        href={`tel:${lead.candidate_phone}`}
                                                        className="text-indigo-600 hover:text-indigo-800 hover:underline font-semibold"
                                                    >
                                                        {lead.candidate_phone}
                                                    </a>
                                                ) : 'N/A'}
                                            </td>

                                            {/* Application Date */}
                                            <td className="p-2 text-center border border-slate-200 text-sm">{formatDateTime(lead.Created_Date)}</td>

                                            {/* Source of Application */}
                                            <td className="p-2 text-center capitalize border border-slate-200 text-sm">{lead.source || 'N/A'}</td>

                                            {/* Position Applied For */}
                                            <td className="p-2 text-center font-semibold text-slate-700 border border-slate-200 text-sm">{lead.candidate_designation || 'N/A'}</td>

                                            {/* Priority Badge */}
                                            {/* <td className="p-2 text-center border border-slate-200 text-sm">
                                                <span className={`text-[0.6rem] font-bold uppercase tracking-wider ${getPriorityBadgeClass(currentPriority)}`}>
                                                    {currentPriority}
                                                </span>
                                            </td> */}

                                            {/* Lead Stage Badge Dropdown */}
                                            <td className="p-2 text-center border border-slate-200 text-sm relative">
                                                <div className="relative inline-block text-left stage-dropdown-container">
                                                    <button
                                                        onClick={() => setActiveDropdownLeadId(activeDropdownLeadId === lead.id ? null : lead.id)}
                                                        className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border shadow-sm flex items-center gap-1 mx-auto transition-all duration-200 hover:scale-105  ${getStageClass(lead)}`}
                                                    >
                                                        {getStageLabel(lead)}
                                                        <i className="fas fa-chevron-down text-[8px] opacity-70"></i>
                                                    </button>
                                                    {activeDropdownLeadId === lead.id && (
                                                        <div className={`absolute right-0 w-44 rounded-lg shadow-xl bg-white border border-slate-100 z-50 max-h-60 overflow-y-auto ${dropdownPlacementClass}`}>
                                                            <div className="py-1">
                                                                {(lead.client_name || lead.client_phone || lead.client_company_name ? CLIENT_STAGES : INTERVIEW_STAGES).map((stage) => (
                                                                    <button
                                                                        key={stage.code}
                                                                        onClick={() => {
                                                                            handleStageSelect(lead, stage.code);
                                                                            setActiveDropdownLeadId(null);
                                                                        }}
                                                                        className={`block w-full text-left px-3 py-2 text-xs text-slate-700 hover:bg-slate-100 transition-all font-semibold ${(lead.lead_stage || lead.interview_status || lead.client_status) === stage.code ? 'bg-indigo-50 text-indigo-700 border-l-2 border-indigo-600' : ''}`}
                                                                    >
                                                                        {stage.display_name}
                                                                    </button>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </td>

                                            {/* Current Status sub-status Pill */}
                                            <td className="p-2 text-center border border-slate-200 text-sm whitespace-nowrap">
                                                {getStatusLabel(lead) !== '—' ? (
                                                    <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border shadow-sm inline-block ${getStatusClass(lead)}`}>
                                                        {getStatusLabel(lead)}
                                                    </span>
                                                ) : (
                                                    <span className="text-slate-400 font-medium">—</span>
                                                )}
                                            </td>

                                            {/* Interview Status Badge Dropdown */}
                                            <td className="p-2 text-center border border-slate-200 text-sm relative">
                                                {(lead.lead_stage === 'follow_up' && lead.interview_attendance) ? (
                                                    <div className="relative inline-block text-left interview-status-dropdown-container">
                                                        <button
                                                            onClick={() => setActiveDropdownInterviewId(activeDropdownInterviewId === lead.id ? null : lead.id)}
                                                            className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border shadow-sm flex items-center gap-1 mx-auto transition-all duration-200 hover:scale-105 whitespace-nowrap ${lead.interview_attendance === 'Attended'
                                                                    ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                                                                    : 'bg-amber-50 text-amber-700 border-amber-200'
                                                                }`}
                                                        >
                                                            {lead.interview_attendance}
                                                            <i className="fas fa-chevron-down text-[8px] opacity-70"></i>
                                                        </button>
                                                        {activeDropdownInterviewId === lead.id && (
                                                            <div className={`absolute right-0 w-32 rounded-lg shadow-xl bg-white border border-slate-100 z-50`}>
                                                                <div className="py-1">
                                                                    {(lead.interview_attendance === 'Attended' ? ['Attended'] : ['Scheduled', 'Attended']).map((statusOption) => (
                                                                        <button
                                                                            key={statusOption}
                                                                            onClick={() => {
                                                                                if (statusOption === 'Scheduled' && lead.interview_attendance === 'Attended') return;
                                                                                handleInterviewAttendanceSelect(lead, statusOption);
                                                                                setActiveDropdownInterviewId(null);
                                                                            }}
                                                                            className={`block w-full text-left px-3 py-2 text-xs text-slate-700 hover:bg-slate-100 transition-all font-semibold ${lead.interview_attendance === statusOption ? 'bg-indigo-50 text-indigo-700' : ''}`}
                                                                        >
                                                                            {statusOption}
                                                                        </button>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <span className="text-slate-400 font-medium">—</span>
                                                )}
                                            </td>

                                            {/* Interview Scheduled Date */}
                                            <td className="p-2 text-center border border-slate-200 text-sm">
                                                {lead.interview_scheduled_date ? (
                                                    <span className="font-semibold text-slate-700">
                                                        {formatDateTime(lead.interview_scheduled_date)}
                                                    </span>
                                                ) : (
                                                    <span className="text-xs text-slate-400">—</span>
                                                )}
                                            </td>

                                            {/* Handled By */}
                                            <td className="p-2 text-center border border-slate-200 text-sm">{lead.handled_by || 'Unassigned'}</td>

                                            {/* Assigned By */}
                                            <td className="p-2 text-center border border-slate-200 text-sm">{lead.assigned_by || 'System'}</td>

                                            {/* Remarks */}
                                            <td className="p-2 text-center border border-slate-200 text-sm">
                                                {lead.interview_call_remarks ? (
                                                    <OverlayTrigger
                                                        trigger="click"
                                                        placement="top"
                                                        rootClose
                                                        overlay={
                                                            <Tooltip id={`tooltip-remarks-${lead.id}`}>
                                                                {lead.interview_call_remarks}
                                                            </Tooltip>
                                                        }
                                                    >
                                                        <span className="block truncate max-w-[150px] mx-auto text-xs cursor-pointer text-indigo-600 hover:text-indigo-800 font-semibold hover:underline">
                                                            {lead.interview_call_remarks}
                                                        </span>
                                                    </OverlayTrigger>
                                                ) : (
                                                    <span className="text-xs text-slate-400">—</span>
                                                )}
                                            </td>

                                            {/* Next Follow-up Scheduler */}
                                            <td className="p-2 text-center border border-slate-200 text-sm">
                                                {lead.next_followup_date ? (
                                                    <div
                                                        onClick={() => handleOpenFollowupModal(lead)}
                                                        className="text-xs cursor-pointer group hover:bg-slate-50 p-1.5 rounded-lg border border-dashed border-transparent hover:border-indigo-200 transition-all flex flex-col items-center justify-center max-w-[130px] mx-auto"
                                                        title="Click to edit follow-up"
                                                    >
                                                        <span className="font-semibold text-slate-700 block group-hover:text-indigo-600">
                                                            {new Date(lead.next_followup_date).toLocaleDateString('en-GB').replace(/\//g, '-')}
                                                        </span>
                                                        <span className="text-[10px] text-slate-400 block group-hover:text-indigo-500 font-medium">
                                                            {lead.next_followup_time ?
                                                                new Date(`1970-01-01T${lead.next_followup_time}`).toLocaleTimeString('en-US', {
                                                                    hour: 'numeric',
                                                                    minute: '2-digit',
                                                                    hour12: true
                                                                })
                                                                : ''}
                                                        </span>
                                                    </div>
                                                ) : (
                                                    <button
                                                        onClick={() => handleOpenFollowupModal(lead)}
                                                        className="px-2.5 py-1 text-[10px] text-indigo-600 hover:text-indigo-700 bg-indigo-50 hover:bg-indigo-100 rounded-md transition-all border border-dashed border-indigo-200 hover:border-indigo-300 font-bold flex items-center justify-center gap-1 mx-auto shadow-sm"
                                                    >
                                                        <i className="fas fa-calendar-plus text-[9px]"></i> Schedule
                                                    </button>
                                                )}
                                            </td>

                                            {/* Action Edit button */}
                                            <td className="p-2 text-center border border-slate-200 text-sm">
                                                <button
                                                    onClick={() => handleOpenActionModal(lead)}
                                                    className="px-2 py-1 rounded bg-blue-500 text-white text-[10px] font-semibold hover:bg-orange-600 transition-all flex items-center justify-center gap-1 mx-auto"
                                                >
                                                    <i className="fas fa-edit text-[9px]"></i> Edit
                                                </button>
                                            </td>

                                            {/* Details View button */}
                                            {/* <td className="p-2 text-center border border-slate-200 text-sm">
                                                <button
                                                    onClick={() => navigate(`/activity/lead-log/${lead.id}`)}
                                                    className="px-2 py-1 rounded bg-blue-600 text-white text-[10px] font-semibold hover:bg-blue-700 transition-all"
                                                >
                                                    View
                                                </button>
                                            </td> */}
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                )}
            </main>

            {/* Pagination & Action Footer */}
            {(totalCount > 0) && (
                <div className="d-flex justify-content-between align-items-center mt-2 bg-white p-2 rounded shadow-sm font-Poppins flex-wrap gap-3">
                    {/* Left: Total */}
                    <div className="text-sm font-semibold text-slate-600">
                        Total: <strong className="text-indigo-600">{totalCount}</strong>
                    </div>

                    {/* Center: Pagination Buttons */}
                    <div className="d-flex align-items-center gap-2">
                        <button
                            className="btn btn-primary btn-sm rounded px-3"
                            disabled={page === 1}
                            onClick={() => setPage(prev => Math.max(prev - 1, 1))}
                        >
                            Previous
                        </button>

                        <div className="small px-3 bg-primary text-white rounded py-1 font-semibold">
                            {page} of {totalPages}
                        </div>

                        <button
                            className="btn btn-primary btn-sm rounded px-3"
                            disabled={page === totalPages}
                            onClick={() => setPage(prev => Math.min(prev + 1, totalPages))}
                        >
                            Next
                        </button>
                    </div>

                    {/* Center-Right: Page Jump */}
                    <div className="d-flex align-items-center gap-2">
                        <span className="text-sm">Page</span>
                        <input
                            type="number"
                            className="form-control form-control-sm"
                            style={{ width: "55px" }}
                            min={1}
                            max={totalPages}
                            value={page}
                            onChange={(e) => {
                                const val = parseInt(e.target.value);
                                const maxLimit = totalPages;
                                if (val >= 1 && val <= maxLimit) {
                                    setPage(val);
                                }
                            }}
                        />
                    </div>
                </div>
            )}

            {/* Action Modal for Edit Candidate Lead */}
            {showActionModal && selectedLeadForModal && (
                <Modal show={showActionModal} onHide={() => setShowActionModal(false)} centered>
                    <Modal.Header closeButton className="border-0 pb-0">
                        <Modal.Title className="text-lg font-bold text-slate-800">
                            Edit Candidate Lead
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="pt-2">
                        <div className="space-y-4">
                            {/* Candidate Info Editable Fields Grid */}
                            <div className="bg-slate-50 p-3 rounded-lg border border-slate-100 text-sm">
                                <span className="text-xs font-bold text-indigo-600 block mb-2.5 uppercase tracking-wider">Candidate Details</span>
                                <div className="grid grid-cols-2 gap-2.5">
                                    <div className="col-span-2">
                                        <label className="text-slate-500 font-semibold text-[11px] mb-0.5 block">Candidate Name</label>
                                        <input
                                            type="text"
                                            value={modalCandidateName}
                                            onChange={(e) => setModalCandidateName(e.target.value)}
                                            className="form-control border border-slate-200 rounded-lg px-2 py-1.5 text-xs text-slate-700 focus:ring-2 focus:ring-indigo-500 w-full"
                                        />
                                    </div>
                                    <div className="col-span-2">
                                        <label className="text-slate-500 font-semibold text-[11px] mb-0.5 block">Email ID</label>
                                        <input
                                            type="email"
                                            value={modalCandidateEmail}
                                            onChange={(e) => setModalCandidateEmail(e.target.value)}
                                            className="form-control border border-slate-200 rounded-lg px-2 py-1.5 text-xs text-slate-700 focus:ring-2 focus:ring-indigo-500 w-full"
                                        />
                                    </div>
                                    <div className="col-span-2 md:col-span-1">
                                        <label className="text-slate-500 font-semibold text-[11px] mb-0.5 block">Mobile Number</label>
                                        <input
                                            type="text"
                                            value={modalCandidatePhone}
                                            onChange={(e) => setModalCandidatePhone(e.target.value)}
                                            className="form-control border border-slate-200 rounded-lg px-2 py-1.5 text-xs text-slate-700 focus:ring-2 focus:ring-indigo-500 w-full"
                                        />
                                    </div>
                                    <div className="col-span-2 md:col-span-1">
                                        <label className="text-slate-500 font-semibold text-[11px] mb-0.5 block">Source</label>
                                        <input
                                            type="text"
                                            value={modalSource}
                                            onChange={(e) => setModalSource(e.target.value)}
                                            className="form-control border border-slate-200 rounded-lg px-2 py-1.5 text-xs text-slate-700 focus:ring-2 focus:ring-indigo-500 w-full"
                                        />
                                    </div>
                                    <div className="col-span-2">
                                        <label className="text-slate-500 font-semibold text-[11px] mb-0.5 block">Position Applied</label>
                                        <input
                                            type="text"
                                            value={modalCandidateDesignation}
                                            onChange={(e) => setModalCandidateDesignation(e.target.value)}
                                            className="form-control border border-slate-200 rounded-lg px-2 py-1.5 text-xs text-slate-700 focus:ring-2 focus:ring-indigo-500 w-full"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Priority */}
                            {/* <div>
                                <label className="form-label text-xs font-semibold text-slate-600 mb-1">Priority</label>
                                <select
                                    value={modalPriority}
                                    onChange={(e) => setModalPriority(e.target.value)}
                                    className="form-select border border-slate-200 rounded-lg px-3 py-2 text-xs text-slate-700 focus:ring-2 focus:ring-indigo-500 w-full"
                                >
                                    <option value="high">High</option>
                                    <option value="medium">Medium</option>
                                    <option value="low">Low</option>
                                </select>
                            </div> */}

                            {/* Recruiter (Visible only to Admin/HR or permitted users) */}
                            {canAssignLeads && (
                                <div>
                                    <label className="form-label text-xs font-semibold text-slate-600 mb-1">Assigned Recruiter</label>
                                    <select
                                        value={modalRecruiter}
                                        onChange={(e) => setModalRecruiter(e.target.value)}
                                        className="form-select border border-slate-200 rounded-lg px-3 py-2 text-xs text-slate-700 focus:ring-2 focus:ring-indigo-500 w-full"
                                    >
                                        <option value="">-- Select Recruiter --</option>
                                        {recruiters.map((rec) => (
                                            <option key={rec.id} value={rec.EmployeeId}>
                                                {rec.Name} ({rec.EmployeeId})
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {/* Remarks */}
                            {/* <div>
                                <label className="form-label text-xs font-semibold text-slate-600 mb-1">Remarks</label>
                                <textarea
                                    value={modalRemarks}
                                    onChange={(e) => setModalRemarks(e.target.value)}
                                    placeholder="Enter conversation details..."
                                    rows={3}
                                    className="form-control border border-slate-200 rounded-lg px-3 py-2 text-xs text-slate-700 focus:ring-2 focus:ring-indigo-500 w-full resize-none"
                                />
                            </div> */}
                        </div>
                    </Modal.Body>
                    <Modal.Footer className="border-0 pt-0">
                        <button
                            onClick={() => setShowActionModal(false)}
                            className="px-4 py-2 text-sm font-semibold text-slate-500 hover:text-slate-700 rounded-lg bg-slate-100 hover:bg-slate-200 transition-all mr-2"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSaveModalChanges}
                            disabled={isSavingModal}
                            className="px-4 py-2 text-sm font-semibold text-white rounded-lg bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-200 disabled:text-slate-400 transition-all"
                        >
                            {isSavingModal ? 'Saving...' : 'Save Changes'}
                        </button>
                    </Modal.Footer>
                </Modal>
            )}
            {showReviewModal && reviewLead && (
                <Modal show={showReviewModal} onHide={() => { setShowReviewModal(false); setReviewLead(null); }} centered>
                    <Modal.Header closeButton className="border-0 pb-0">
                        <Modal.Title className="text-lg font-bold text-slate-800">
                            Update Stage to: <span className="text-indigo-600">{formatStageName(reviewNewStage, stages.find(s => s.code === reviewNewStage)?.display_name)}</span>
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="pt-2">
                        <div className="space-y-4 font-popins poppins">
                            {/* Current Status sub-selection dropdown */}
                            {SUB_STATUSES[reviewNewStage] && (
                                <div>
                                    <label className="form-label text-xs font-semibold text-slate-600 mb-1">Current Status *</label>
                                    <select
                                        value={reviewCurrentStatus}
                                        onChange={(e) => setReviewCurrentStatus(e.target.value)}
                                        className="form-select border border-slate-200 rounded-lg px-3 py-2 text-xs text-slate-700 focus:ring-2 focus:ring-indigo-500 w-full"
                                    >
                                        <option value="">-- Select Current Status --</option>
                                        {SUB_STATUSES[reviewNewStage].map((sub) => (
                                            <option key={sub.code} value={sub.code}>
                                                {sub.display_name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            {/* Remarks */}
                            <div>
                                <label className="form-label text-xs font-semibold text-slate-600 mb-1">Stage Change Remarks</label>
                                <textarea
                                    value={reviewRemarks}
                                    onChange={(e) => setReviewRemarks(e.target.value)}
                                    placeholder="Enter remarks for changing stage..."
                                    rows={3}
                                    className="form-control border border-slate-200 rounded-lg px-3 py-2 text-xs text-slate-700 focus:ring-2 focus:ring-indigo-500 w-full resize-none"
                                />
                            </div>

                             {/* Interview Scheduled Date Selection */}
                             {(reviewNewStage === 'interview_scheduled' || reviewCurrentStatus === 'interview_scheduled') && (
                                <div className="bg-indigo-50/50 p-3 rounded-lg border border-indigo-100 text-sm space-y-2">
                                    <span className="text-xs font-bold text-slate-700 block uppercase tracking-wider">Interview Scheduled Details</span>
                                    <div>
                                        <label className="text-slate-500 font-semibold text-[11px] mb-0.5 block">Interview Date & Time *</label>
                                        <input
                                            type="datetime-local"
                                            value={reviewInterviewDate}
                                            onChange={(e) => setReviewInterviewDate(e.target.value)}
                                            className="form-control border border-slate-200 rounded-lg px-3 py-1.5 text-xs text-slate-700 focus:ring-2 focus:ring-indigo-500 w-full bg-white"
                                        />
                                    </div>
                                </div>
                            )}

                            {/* Schedule Follow-up Checkbox */}
                            <div className="flex items-center gap-2 py-1.5 border-t border-b border-slate-100 my-2">
                                <input
                                    type="checkbox"
                                    id="reviewScheduleFollowup"
                                    checked={reviewScheduleFollowup}
                                    onChange={(e) => setReviewScheduleFollowup(e.target.checked)}
                                    className="rounded border-slate-300 text-indigo-600 focus:ring-indigo-500 h-4 w-4"
                                />
                                <label htmlFor="reviewScheduleFollowup" className="text-xs font-bold text-slate-700 cursor-pointer select-none mb-0">
                                    Schedule Next Follow-Up
                                </label>
                            </div>

                            {/* Optional Follow-up Scheduling Section */}
                            {reviewScheduleFollowup && (
                                <div className="bg-indigo-50/50 p-3 rounded-lg border border-indigo-100 text-sm space-y-3 animate-fadeIn">
                                    <span className="text-xs font-bold text-indigo-600 block uppercase tracking-wider">Follow-Up Schedule Details</span>
                                    <div className="grid grid-cols-2 gap-2.5">
                                        <div className="col-span-2 md:col-span-1">
                                            <label className="text-slate-500 font-semibold text-[11px] mb-0.5 block">Expected Date</label>
                                            <input
                                                type="date"
                                                value={reviewFollowupDate}
                                                onChange={(e) => setReviewFollowupDate(e.target.value)}
                                                className="form-control border border-slate-200 rounded-lg px-2 py-1.5 text-xs text-slate-700 focus:ring-2 focus:ring-indigo-500 w-full bg-white"
                                            />
                                        </div>
                                        <div className="col-span-2 md:col-span-1">
                                            <label className="text-slate-500 font-semibold text-[11px] mb-0.5 block">Expected Time</label>
                                            <input
                                                type="time"
                                                value={reviewFollowupTime}
                                                onChange={(e) => setReviewFollowupTime(e.target.value)}
                                                className="form-control border border-slate-200 rounded-lg px-2 py-1.5 text-xs text-slate-700 focus:ring-2 focus:ring-indigo-500 w-full bg-white"
                                            />
                                        </div>

                                        <div className="col-span-2">
                                            <label className="text-slate-500 font-semibold text-[11px] mb-0.5 block">Follow-Up Notes</label>
                                            <textarea
                                                value={reviewFollowupNotes}
                                                onChange={(e) => setReviewFollowupNotes(e.target.value)}
                                                placeholder="Add follow-up details..."
                                                rows={2}
                                                className="form-control border border-slate-200 rounded-lg px-3 py-2 text-xs text-slate-700 focus:ring-2 focus:ring-indigo-500 w-full resize-none bg-white"
                                            />
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </Modal.Body>
                    <Modal.Footer className="border-0 pt-0">
                        <button
                            onClick={() => { setShowReviewModal(false); setReviewLead(null); }}
                            className="px-4 py-2 text-sm font-semibold text-slate-500 hover:text-slate-700 rounded-lg bg-slate-100 hover:bg-slate-200 transition-all mr-2"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSaveReview}
                            disabled={isSavingReview}
                            className="px-4 py-2 text-sm font-semibold text-white rounded-lg bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-200 disabled:text-slate-400 transition-all"
                        >
                            {isSavingReview ? 'Saving...' : 'Save Stage'}
                        </button>
                    </Modal.Footer>
                </Modal>
            )}

            {showFollowupModal && followupLead && (
                <Modal show={showFollowupModal} onHide={() => { setShowFollowupModal(false); setFollowupLead(null); }} centered>
                    <Modal.Header closeButton className="border-0 pb-0">
                        <Modal.Title className="text-lg font-bold text-slate-800">
                            {followupLead.next_followup_date ? 'Edit Follow-Up' : 'Schedule Follow-Up'}
                        </Modal.Title>
                    </Modal.Header>
                    <Modal.Body className="pt-2">
                        <div className="space-y-4 font-popins poppins">
                            <div className="bg-slate-50 p-3 rounded-lg border border-slate-100 text-sm">
                                <div className="grid grid-cols-2 gap-2.5">
                                    <div className="col-span-2 md:col-span-1">
                                        <label className="text-slate-500 font-semibold text-[11px] mb-0.5 block">Expected Date</label>
                                        <input
                                            type="date"
                                            value={followupDate}
                                            onChange={(e) => setFollowupDate(e.target.value)}
                                            className="form-control border border-slate-200 rounded-lg px-2 py-1.5 text-xs text-slate-700 focus:ring-2 focus:ring-indigo-500 w-full bg-white"
                                        />
                                    </div>
                                    <div className="col-span-2 md:col-span-1">
                                        <label className="text-slate-500 font-semibold text-[11px] mb-0.5 block">Expected Time</label>
                                        <input
                                            type="time"
                                            value={followupTime}
                                            onChange={(e) => setFollowupTime(e.target.value)}
                                            className="form-control border border-slate-200 rounded-lg px-2 py-1.5 text-xs text-slate-700 focus:ring-2 focus:ring-indigo-500 w-full bg-white"
                                        />
                                    </div>
                                    <div className="col-span-2">
                                        <label className="text-slate-500 font-semibold text-[11px] mb-0.5 block">Follow-Up Notes</label>
                                        <textarea
                                            value={followupNotes}
                                            onChange={(e) => setFollowupNotes(e.target.value)}
                                            placeholder="Enter conversation details or next actions..."
                                            rows={3}
                                            className="form-control border border-slate-200 rounded-lg px-3 py-2 text-xs text-slate-700 focus:ring-2 focus:ring-indigo-500 w-full resize-none bg-white"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </Modal.Body>
                    <Modal.Footer className="border-0 pt-0">
                        <button
                            onClick={() => { setShowFollowupModal(false); setFollowupLead(null); }}
                            className="px-4 py-2 text-sm font-semibold text-slate-500 hover:text-slate-700 rounded-lg bg-slate-100 hover:bg-slate-200 transition-all mr-2"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleSaveFollowup}
                            disabled={isSavingFollowup}
                            className="px-4 py-2 text-sm font-semibold text-white rounded-lg bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-200 disabled:text-slate-400 transition-all"
                        >
                            {isSavingFollowup ? 'Saving...' : 'Save Follow-Up'}
                        </button>
                    </Modal.Footer>
                </Modal>
            )}

            {showTimelineModal && (
                <Modal
                    show={showTimelineModal}
                    onHide={() => setShowTimelineModal(false)}
                    centered
                    dialogClassName="max-w-[420px]"
                >
                    <div className="bg-white rounded-2xl overflow-hidden shadow-2xl p-6 border border-slate-100 poppins">
                        {/* Header */}
                        <div className="flex justify-between items-start mb-4">
                            <div>
                                <h3 className="text-xl font-bold text-slate-800 m-0">Timeline Selection</h3>
                                <span className="text-[10px] font-bold text-indigo-600 uppercase tracking-widest block mt-1">
                                    Currently: {getFilterTypeDisplayName().toUpperCase()}
                                </span>
                            </div>
                            <button
                                onClick={() => setShowTimelineModal(false)}
                                className="text-slate-400 hover:text-slate-600 bg-slate-50 hover:bg-slate-100 rounded-full w-7 h-7 flex items-center justify-center transition-all border-0 focus:outline-none"
                            >
                                <i className="fas fa-times"></i>
                            </button>
                        </div>

                        {/* Presets Grid */}
                        <div className="grid grid-cols-3 gap-2 mb-4">
                            {[
                                { code: 'today', label: 'Today' },
                                { code: 'yesterday', label: 'Yesterday' },
                                { code: 'this_week', label: 'This Week' },
                                { code: 'last_week', label: 'Last Week' },
                                { code: 'this_month', label: 'This Month' },
                                { code: 'prev_month', label: 'Last Month' },
                                { code: 'all', label: 'Timeline' }
                            ].map((preset) => {
                                const isActive = localFilterType === preset.code || (preset.code === 'all' && (localFilterType === 'all_time' || localFilterType === 'timeline'));
                                return (
                                    <button
                                        key={preset.code}
                                        onClick={() => {
                                            setLocalFilterType(preset.code);
                                            setLocalStartDate(getCalculatedDateString(preset.code, 'start'));
                                            setLocalEndDate(getCalculatedDateString(preset.code, 'end'));
                                        }}
                                        className={`py-2 text-[11px] font-bold uppercase tracking-wider rounded-xl transition-all duration-200 border text-center ${preset.code === 'all' ? 'col-span-3' : ''
                                            } ${isActive
                                                ? 'bg-indigo-600 border-indigo-600 text-white shadow-md shadow-indigo-100'
                                                : 'bg-slate-50 border-slate-200 text-slate-700 hover:bg-indigo-50 hover:text-indigo-600 hover:border-indigo-200'
                                            }`}
                                    >
                                        {preset.label}
                                    </button>
                                );
                            })}
                        </div>

                        {/* Divider */}
                        <hr className="border-slate-100 my-4" />

                        {/* Flux Range Section */}
                        <div className="mb-4">
                            <div className="flex justify-between items-center mb-3">
                                <h4 className="text-xs font-bold text-slate-800 uppercase tracking-widest m-0">Flux Range</h4>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setLocalFilterType('today');
                                        setLocalStartDate(getCalculatedDateString('today', 'start'));
                                        setLocalEndDate(getCalculatedDateString('today', 'end'));
                                        setLocalStartTime(null);
                                        setLocalEndTime(null);
                                    }}
                                    className="text-[11px] font-bold text-rose-500 hover:text-rose-700 bg-transparent border-0 outline-none p-0 cursor-pointer uppercase tracking-wider transition-colors"
                                >
                                    Reset
                                </button>
                            </div>

                            <div className="grid grid-cols-2 gap-3 mb-3">
                                <div className="flex flex-col gap-1">
                                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Start Date</label>
                                    <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-2 focus-within:border-indigo-500 focus-within:ring-1 focus-within:ring-indigo-500 transition-all">
                                        <input
                                            type="date"
                                            value={localStartDate}
                                            onChange={(e) => handleCustomDateChange(true, e.target.value)}
                                            className="border-0 bg-transparent text-xs text-slate-700 font-semibold outline-none focus:ring-0 p-0 w-full"
                                        />
                                    </div>
                                </div>
                                <div className="flex flex-col gap-1">
                                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">End Date</label>
                                    <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-2 focus-within:border-indigo-500 focus-within:ring-1 focus-within:ring-indigo-500 transition-all">
                                        <input
                                            type="date"
                                            value={localEndDate}
                                            onChange={(e) => handleCustomDateChange(false, e.target.value)}
                                            className="border-0 bg-transparent text-xs text-slate-700 font-semibold outline-none focus:ring-0 p-0 w-full"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <div className="flex flex-col gap-1">
                                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Start Time</label>
                                    <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-2 focus-within:border-indigo-500 focus-within:ring-1 focus-within:ring-indigo-500 transition-all">
                                        <input
                                            type="time"
                                            value={localStartTime || ''}
                                            onChange={(e) => setLocalStartTime(e.target.value || null)}
                                            className="border-0 bg-transparent text-xs text-slate-700 font-semibold outline-none focus:ring-0 p-0 w-full"
                                        />
                                    </div>
                                </div>
                                <div className="flex flex-col gap-1">
                                    <label className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">End Time</label>
                                    <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-2 focus-within:border-indigo-500 focus-within:ring-1 focus-within:ring-indigo-500 transition-all">
                                        <input
                                            type="time"
                                            value={localEndTime || ''}
                                            onChange={(e) => setLocalEndTime(e.target.value || null)}
                                            className="border-0 bg-transparent text-xs text-slate-700 font-semibold outline-none focus:ring-0 p-0 w-full"
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Actions */}
                        <button
                            onClick={() => {
                                setFilterType(localFilterType);
                                if (localFilterType === 'custom') {
                                    setCustomStartDate(localStartDate);
                                    setCustomEndDate(localEndDate);
                                } else if (localFilterType === 'all') {
                                    setCustomStartDate('');
                                    setCustomEndDate('');
                                } else {
                                    setCustomStartDate(localStartDate);
                                    setCustomEndDate(localEndDate);
                                }
                                setStartTime(localStartTime || '');
                                setEndTime(localEndTime || '');
                                setPage(1);
                                setShowTimelineModal(false);
                            }}
                            className="w-full py-3 bg-[#2563EB] hover:bg-[#004ef8] text-white font-bold text-xs rounded-xl tracking-widest transition-all duration-200 uppercase mt-2 shadow-lg hover:shadow-xl active:scale-[0.98]"
                        >
                            Sync Range
                        </button>
                    </div>
                </Modal>
            )}
            {showWAModal && (
                <SendWhatsAppModal
                    show={showWAModal}
                    onClose={() => setShowWAModal(false)}
                    selectedLeads={Object.values(selectedLeadsData)}
                />
            )}
        </div>
    );
};

export default LeadsDashboard;
